import type { UserSettings } from '@respiria/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Celebration } from '@/features/achievements/Celebration';
import { ImpersonationBanner } from '@/features/admin/ImpersonationBanner';
import { TourProvider } from '@/features/tour/TourProvider';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { applyAppearance, watchSystemTheme } from '@/lib/appearance';
import { ConnectionStatus } from './ConnectionStatus';
import { TabBar } from './TabBar';

/** Mobile-first shell: centred column, bottom nav, safe-area aware. */
export function AppLayout() {
  // Apply the saved theme and reduced-motion as soon as we know them, so the
  // app isn't stuck light for a logged-in user who picked dark.
  const { data } = useQuery({
    queryKey: queryKeys.settings(),
    queryFn: () => api<{ settings: UserSettings }>('/api/settings'),
  });

  useEffect(() => {
    if (!data?.settings) return;
    applyAppearance(data.settings.theme, data.settings.reducedMotion);
    watchSystemTheme(
      () => data.settings.theme,
      () => data.settings.reducedMotion,
    );
  }, [data?.settings]);

  // Keep <html lang> in sync so screen readers announce content in the right
  // language. 'ca-valencia' maps to the BCP-47 variant tag.
  useEffect(() => {
    const lang = data?.settings?.language;
    if (!lang) return;
    document.documentElement.lang = lang === 'ca-valencia' ? 'ca-ES-valencia' : lang;
  }, [data?.settings?.language]);

  return (
    <TourProvider>
      <div className="min-h-dvh bg-bg">
        <ConnectionStatus />
        <ImpersonationBanner />
        <main
          className="mx-auto max-w-[var(--content-max)] px-4 pt-[max(env(safe-area-inset-top),16px)]"
          style={{ paddingBottom: 'calc(var(--tabbar-height) + env(safe-area-inset-bottom) + 32px)' }}
        >
          <Outlet />
        </main>
        <TabBar />
        {/* Mounted once here so an unlock earned anywhere surfaces on any screen. */}
        <Celebration />
      </div>
    </TourProvider>
  );
}
